import { useParams } from "react-router-dom";
import { useGetServiceAccount } from "@/queries/service-account-queries";
import { useContext, useEffect, type FC } from "react";
import { SettingsPageContext } from "@/contexts/SettingsPageContext";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { Text } from "@/components/podkit/typography/Text";
import { serviceAccountsUrl } from "@/utils/service-accounts";
import { ServiceAccountSummary } from "@/components/settings/organization/service-accounts/ServiceAccountSummary";
import { ServiceAccountGitAuthentications } from "@/components/settings/organization/service-accounts/ServiceAccountGitAuthentications";
import { ServiceAccountIntegrations } from "@/components/settings/organization/service-accounts/ServiceAccountIntegrations";

export const ServiceAccountDetailsPage: FC = () => {
    const { setCustomBreadcrumbs } = useContext(SettingsPageContext);
    const { serviceAccountId } = useParams<{ serviceAccountId: string }>();
    const {
        data: serviceAccount,
        isPending,
        isError,
    } = useGetServiceAccount({ serviceAccountId: serviceAccountId || "", enabled: !!serviceAccountId });

    useEffect(() => {
        if (!serviceAccount) {
            setCustomBreadcrumbs([{ label: "Service Accounts", to: serviceAccountsUrl() }]);
            return;
        }

        setCustomBreadcrumbs([{ label: "Service Accounts", to: serviceAccountsUrl() }, { label: serviceAccount.name }]);

        return () => {
            setCustomBreadcrumbs(null);
        };
    }, [serviceAccount, setCustomBreadcrumbs]);

    if (isPending) {
        return <LoadingState />;
    }

    if (!serviceAccountId) {
        return null;
    }

    if (isError || !serviceAccount) {
        return (
            <div className="mt-6 flex flex-col items-center justify-center">
                <Text className="text-xl font-semibold text-content-primary">Service account not found</Text>
                <Text className="text-content-secondary">
                    The service account you&apos;re looking for doesn&apos;t exist or you don&apos;t have access to it.
                </Text>
            </div>
        );
    }

    return (
        <div className="flex h-full max-w-[480px] flex-col gap-4 pt-6">
            <ServiceAccountSummary serviceAccountId={serviceAccountId} />
            <div className="flex-grow border-b border-border-subtle" aria-hidden="true" />
            <ServiceAccountGitAuthentications serviceAccountId={serviceAccountId} />
            <div className="flex-grow border-b border-border-subtle" aria-hidden="true" />
            <ServiceAccountIntegrations serviceAccountId={serviceAccountId} />
        </div>
    );
};
